import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Box, IconButton, Typography,
  Alert, Chip, Fade, Tooltip, Paper, CircularProgress, LinearProgress
} from '@mui/material';
import {
  Send as SendIcon,
  StopCircle as StopIcon,
  AutoAwesome as SparkleIcon,
  Psychology as ThinkingIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  KeyboardArrowDown as JumpToPresentIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { ChatMessage } from './ChatMessage';
import { SessionDivider } from './SessionDivider';
import { SessionsPanel } from './SessionsPanel';
import { NewSessionGreeting } from './NewSessionGreeting';
import ChatMessageInput, { ChatMessageInputHandle } from './ChatMessageInput';
import { AgentChatClient } from '../../services/AgentChatClient';
import { useFileDrop } from '../../hooks/useFileDrop';
import { CHAT_UPLOAD_ACCEPT } from '../../constants/uploadAccept';
import { AgentModelSelection, ChatAttachment, ChatContext, Message } from '../../types/chat';
import { triggerNavigation } from '../../hooks/useNavigationHandler';
import { AI_FILE_UPLOAD_TOOLS } from '../../hooks/useFileChangeListener';
import { useUnifiedThread } from '../../hooks/useUnifiedThread';
import { useClientName } from '../../hooks/useClientName';
import './ChatInterface.css';

interface ChatInterfaceProps {
  context: ChatContext;
  agentClient: AgentChatClient;
  isOpen: boolean;
  setNewChatHandler: (handler: () => void) => void;
  setHistoryHandler: (handler: () => void) => void;
  onNewMessage?: (message: Message, sessionId: string) => void;
  onNotificationPermissionRequest?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onProcessOperations?: (toolOperations: any[]) => void;
  isNavigating?: boolean;
  modelSelection?: AgentModelSelection;
}

// Minimum time the "Loading earlier messages…" indicator stays visible, so a
// fast response doesn't read as a flicker. Purely cosmetic — never delays the
// actual fetch, only how long its indicator is shown for.
const MIN_LOADING_OLDER_VISIBLE_MS = 500;
// How far to nudge the view upward, after the exact scroll position is
// restored, so the user notices new content appeared above.
const LOAD_OLDER_NUDGE_PX = 56;
/** Private attachments allowed on a single message. */
const MAX_CHAT_ATTACHMENTS = 5;

const deriveEmailLink = (operations?: any[]): Message['emailLink'] => {
  if (!Array.isArray(operations)) return null;
  const navigation = operations.find((operation) =>
    operation?.tool === 'navigate_app'
    && operation?.result?.success
    && typeof operation?.result?.route === 'string'
    && /^\/client\/[^/]+\/email(?:\?|$)/.test(operation.result.route),
  );
  if (navigation) return { route: navigation.result.route, state: navigation.result.state };

  const draftOperation = operations.find((operation) => {
    const draft = operation?.result?.data || operation?.result;
    return operation?.tool === 'draft_client_email'
      && (draft?.clientId || operation?.args?.clientId)
      && draft?.subject
      && draft?.body;
  });
  if (!draftOperation) return null;
  const draft = draftOperation.result?.data || draftOperation.result;
  const clientId = draft.clientId || draftOperation.args.clientId;
  const query = new URLSearchParams({
    autoCompose: 'true',
    subject: String(draft.subject),
    ...(draft.emailType ? { emailType: String(draft.emailType) } : {}),
  });
  return {
    route: `/client/${encodeURIComponent(String(clientId))}/email?${query.toString()}`,
    state: { prefillBody: String(draft.body) },
  };
};

const KEYS_THAT_SCROLL_UP = new Set(['Home', 'PageUp', 'ArrowUp']);

const THINKING_STEPS = [
  "Analyzing your request...",
  "Consulting knowledge base...",
  "Checking available tools...",
  "Formulating response...",
  "Running security checks...",
  "Finalizing output..."
];

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  context,
  agentClient,
  isOpen,
  setNewChatHandler,
  setHistoryHandler,
  onNewMessage,
  onNotificationPermissionRequest,
  onLoadingChange,
  onProcessOperations,
  modelSelection,
}) => {
  const {
    blocks,
    currentSessionId,
    isLoadingOlder,
    loadError,
    hasMoreSessions,
    loadOlderSession,
    addMessage,
    replaceLastMessage,
    lastLoadWasCacheHitRef,
    loadedSessionClient,
  } = useUnifiedThread(agentClient, context.sessionId);

  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [thinkingStep, setThinkingStep] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [showJumpToPresent, setShowJumpToPresent] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);
  const [showLoadingOlder, setShowLoadingOlder] = useState(false);
  const [loadedConfirmationVisible, setLoadedConfirmationVisible] = useState(false);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  // The client this conversation is about, as resolved by the server on each
  // turn. Null until a client is established, when the page acts as fallback.
  const [conversationClient, setConversationClient] = useState<{ id: string; name: string } | null>(null);

  const recognitionRef = useRef<any>(null);
  const baseTranscriptRef = useRef<string>(''); // text already in the box before dictation
  const pendingTranscriptRef = useRef<string>('');
  const shouldAutoSendRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatMessageInputHandle>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setNewChatHandler(handleNewChat);
    setHistoryHandler(handleOpenHistory);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      // The panel itself is always mounted and just toggles display via a
      // 0.3s CSS transition (see FloatingChat.tsx) — scrolling before that
      // finishes expanding lands mid-list against the panel's still-small
      // intermediate size, not the true bottom.
      scrollToBottom(350);
      const timer = setTimeout(() => chatInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Scroll to bottom once the initial session block has loaded
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (!initialScrollDoneRef.current && blocks.length > 0) {
      initialScrollDoneRef.current = true;
      scrollToBottom();
    }
  }, [blocks]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (isLoading) return;
      const currentRoute = context.currentRoute || window.location.pathname;
      const currentMessages = blocks.find((block) => block.sessionId === currentSessionId)?.messages || [];
      const latestUser = [...currentMessages].reverse().find((message) => message.role === 'user');
      const latestAssistant = [...currentMessages].reverse().find((message) => message.role === 'assistant');
      try {
        const newSuggestions = await agentClient.getSuggestions({
          currentRoute,
          hasConversation: currentMessages.length > 0,
          latestUserMessage: currentMessages.length ? latestUser?.content : undefined,
          latestAssistantMessage: currentMessages.length ? latestAssistant?.content : undefined,
          latestTools: currentMessages.length
            ? latestAssistant?.toolOperations?.map((operation: any) => operation.tool || operation.toolName).filter(Boolean)
            : undefined,
          pendingTool: currentMessages.length ? latestAssistant?.pendingAction?.tool : undefined,
        });
        setSuggestions(newSuggestions);
      } catch (e) {
        console.error("Error fetching suggestions", e);
      }
    };
    if (isOpen) fetchSuggestions();
  }, [context.currentRoute, isOpen, isLoading, agentClient, blocks, currentSessionId]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading) {
      setThinkingStep(0);
      interval = setInterval(() => {
        setThinkingStep(prev => (prev + 1) % THINKING_STEPS.length);
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    if (isLoading) scrollToBottom();
  }, [thinkingStep, isLoading]);

  const scrollToBottom = (delayMs = 50) => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, delayMs);
  };

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowJumpToPresent(distanceFromBottom > 150);
  }, []);

  const prevScrollHeightRef = useRef<number | null>(null);
  const pendingConfirmationRef = useRef(false);

  // Restore scroll position after older messages are prepended AND apply the
  // "notice me" nudge in the same instant, single correction — runs before
  // paint. Folding both into one write (rather than an exact restore now and
  // a separate smooth nudge later) avoids two competing scroll writes landing
  // at slightly different times, which is what read as jank/lag before.
  useLayoutEffect(() => {
    if (prevScrollHeightRef.current !== null && messagesContainerRef.current) {
      const el = messagesContainerRef.current;
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      el.style.scrollBehavior = 'auto';
      el.scrollTop += delta - LOAD_OLDER_NUDGE_PX;
      el.style.scrollBehavior = '';
      prevScrollHeightRef.current = null;
    }
  }, [blocks]);

  const handleLoadOlder = useCallback(() => {
    prevScrollHeightRef.current = messagesContainerRef.current?.scrollHeight ?? null;
    return loadOlderSession();
  }, [loadOlderSession]);

  // The user's next scroll-up attempt clears the persistent "loaded"
  // confirmation below — whether or not that attempt goes on to start a new
  // load (see onAttempt vs onLoadMore on the hook).
  const handleScrollAttempt = useCallback(() => {
    setLoadedConfirmationVisible(false);
  }, []);

  // Keep the loading indicator visible for a minimum stretch so a fast
  // response doesn't flicker in and out. Purely cosmetic — layered on top of
  // the real isLoadingOlder flag from useUnifiedThread, never delays the fetch.
  const loadingOlderStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (isLoadingOlder) {
      loadingOlderStartedAtRef.current = performance.now();
      setShowLoadingOlder(true);
      return;
    }
    if (loadingOlderStartedAtRef.current === null) {
      setShowLoadingOlder(false);
      return;
    }
    const elapsed = performance.now() - loadingOlderStartedAtRef.current;
    const remaining = Math.max(0, MIN_LOADING_OLDER_VISIBLE_MS - elapsed);
    const timer = setTimeout(() => {
      setShowLoadingOlder(false);
      loadingOlderStartedAtRef.current = null;
    }, remaining);
    return () => clearTimeout(timer);
  }, [isLoadingOlder]);

  // If a block was actually added via a REAL fetch, swap the status text
  // straight to "Earlier messages loaded" and leave it there — no timer, no
  // fade — until the user's next scroll-up attempt clears it
  // (handleScrollAttempt above). A prefetch cache hit (see useUnifiedThread)
  // skips this entirely: it was instant, there was nothing to wait for, so
  // there's nothing worth confirming — the content just appears already in
  // place. If nothing was added at all (e.g. a failed fetch), just clear the
  // pending flag so it can't misfire later on some unrelated blocks change
  // (like sending a new message).
  //
  // Depends on BOTH blocks and showLoadingOlder because a real fetch's
  // "loaded" promotion still waits on showLoadingOlder's minimum-visible hold
  // to settle first, exactly as before.
  const prevBlocksLenRef = useRef(blocks.length);
  // Guards against the very first time blocks populate (the session's own
  // initial hydration, e.g. 0 -> 1 blocks) being mistaken for a real
  // load-earlier fetch — that's a plain load, not a scroll-up pagination
  // event, and showing "Earlier messages loaded" for it is a false positive.
  const initialLoadSettledRef = useRef(false);
  useEffect(() => {
    const isInitialSettle = !initialLoadSettledRef.current;
    initialLoadSettledRef.current = true;

    if (!isInitialSettle && blocks.length > prevBlocksLenRef.current && !lastLoadWasCacheHitRef.current) {
      pendingConfirmationRef.current = true;
    }
    prevBlocksLenRef.current = blocks.length;

    if (showLoadingOlder) return;
    if (pendingConfirmationRef.current) {
      setLoadedConfirmationVisible(true);
    }
    pendingConfirmationRef.current = false;
    prevScrollHeightRef.current = null;
  }, [blocks, showLoadingOlder, lastLoadWasCacheHitRef]);

  // Switching sessions is a clean slate — don't carry a stale "loaded"
  // confirmation (or initial-settle state) over from whatever was previously open.
  //
  // conversationClient is reset here too, and that reset is the whole reason the
  // banner is correct after "+ New chat". It is sticky *within* a conversation on
  // purpose (a turn that establishes no client must leave it alone, and navigating
  // must not silently retarget an existing chat), but stickiness across a session
  // change is just staleness: the banner kept naming the previous session's client
  // while sitting on a different client's page, and only a full page reload — which
  // remounts this component and clears the state — appeared to "fix" it.
  // Clearing it falls back to pageClientName, which is what the server itself
  // resolves for a session with no activeClientId and no history (see
  // AgentChatController's conversationClientId priority chain), so the optimistic
  // value and the next response agree instead of flipping.
  useEffect(() => {
    setLoadedConfirmationVisible(false);
    setConversationClient(null);
    initialLoadSettledRef.current = false;
  }, [currentSessionId]);

  // Detects a "pull to load more" gesture at the top of the message list and
  // fires handleLoadOlder on the FIRST qualifying upward wheel/touch/keyboard
  // gesture while already at the top. Fires on the first qualifying upward
  // scroll/swipe/keypress while already at the top. A ref-based lock (not
  // just showLoadingOlder) is set synchronously the instant a gesture
  // qualifies, so the flurry of wheel/touchmove events within a single
  // continuous gesture can't fire more than one request before React state
  // catches up. The lock releases when handleLoadOlder's own result settles
  // — not tied to showLoadingOlder, since a synchronous cache hit never
  // toggles it at all. While showLoadingOlder is true, further upward scroll
  // intent at the top is actively swallowed (preventDefault) rather than
  // merely ignored — the user physically can't scroll into content that
  // isn't there yet, which removes the jank of a real scroll motion racing
  // the programmatic position correction that runs once the new content lands.
  const touchStartYRef = useRef<number | null>(null);
  const touchCountedRef = useRef(false);
  const triggeredRef = useRef(false);

  // Keep latest hasMoreSessions/showLoadingOlder/handlers in refs so the
  // effect below doesn't need to re-attach listeners every time they change.
  const hasMoreRef = useRef(hasMoreSessions);
  const isLoadingOlderRef = useRef(showLoadingOlder);
  const handleLoadOlderRef = useRef(handleLoadOlder);
  const handleScrollAttemptRef = useRef(handleScrollAttempt);
  useEffect(() => {
    hasMoreRef.current = hasMoreSessions;
    handleLoadOlderRef.current = handleLoadOlder;
    handleScrollAttemptRef.current = handleScrollAttempt;
  });

  useEffect(() => {
    isLoadingOlderRef.current = showLoadingOlder;
  }, [showLoadingOlder]);

  useEffect(() => {
    triggeredRef.current = false;
  }, [currentSessionId]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const topThresholdPx = 8;
    const minTouchDragPx = 12;
    const isAtTop = () => el.scrollTop <= topThresholdPx;

    const tryTrigger = () => {
      handleScrollAttemptRef.current?.();
      if (!hasMoreRef.current || isLoadingOlderRef.current || triggeredRef.current) return;
      // Lock synchronously — before React state has a chance to update —
      // so the rest of this same gesture (many wheel/touchmove events) no-ops.
      // Released exactly when handleLoadOlder's own result settles: a real
      // fetch holds the lock for its full duration (isLoadingOlder also
      // blocks scroll meanwhile), while a synchronous cache hit releases it
      // on the next microtask — instant, but still race-free against the
      // same gesture.
      triggeredRef.current = true;
      Promise.resolve(handleLoadOlderRef.current()).finally(() => {
        triggeredRef.current = false;
      });
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY >= 0) return; // only upward intent
      if (!isAtTop()) return;
      if (isLoadingOlderRef.current) { e.preventDefault(); return; }
      tryTrigger();
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartYRef.current = e.touches[0]?.clientY ?? null;
      touchCountedRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartYRef.current === null) return;
      if (!isAtTop()) return;
      const currentY = e.touches[0]?.clientY ?? touchStartYRef.current;
      const dragDownDistance = currentY - touchStartYRef.current; // finger moving down = revealing older content
      if (dragDownDistance <= 0) return;
      if (isLoadingOlderRef.current) { e.preventDefault(); return; }
      if (touchCountedRef.current) return;
      if (dragDownDistance >= minTouchDragPx) {
        touchCountedRef.current = true;
        tryTrigger();
      }
    };

    const handleTouchEnd = () => {
      touchStartYRef.current = null;
      touchCountedRef.current = false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!KEYS_THAT_SCROLL_UP.has(e.key)) return;
      if (!isAtTop()) return;
      if (isLoadingOlderRef.current) { e.preventDefault(); return; }
      tryTrigger();
    };

    // wheel/touchmove are registered non-passive so isLoadingOlder can
    // preventDefault and actually block the scroll, not just observe it.
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleNewChat = () => {
    setShowHistory(false);
    setSuggestionsHidden(false);
    context.resetSessionId();
    agentClient.resetSession();
    setSuggestions([]);
    const currentRoute = context.currentRoute || window.location.pathname;
    void agentClient.getSuggestions({ currentRoute, hasConversation: false })
      .then(setSuggestions)
      .catch((suggestionError) => console.error("Error fetching new-chat suggestions", suggestionError));
    setTimeout(() => chatInputRef.current?.focus(), 100);
  };

  const handleOpenHistory = () => {
    setShowHistory(true);
  };

  const handleSelectSession = useCallback((sessionId: string) => {
    agentClient.resumeSession(sessionId);
    context.loadSessionId(sessionId);
    // Drop the previous conversation's client so the banner does not carry it
    // into the resumed one. The next turn re-resolves from AgentSession, and
    // until then the page acts as fallback.
    setConversationClient(null);
    setShowHistory(false);
  }, [context, agentClient]);

  const handleSendMessage = useCallback(async (overrideText?: string) => {
    const typedInput = (overrideText ?? chatInputRef.current?.getText() ?? '').trim();
    const readyAttachments = attachments.filter((attachment) => attachment.status === 'ready');
    const currentInput = typedInput || (readyAttachments.length ? 'Please review the attached file(s).' : '');
    if (!currentInput || isLoading || attachments.some((attachment) => attachment.status === 'uploading')) return;

    // Pure frontend shortcut — never reaches the agent. No LLM round-trip is
    // needed just to open the Workflows tab, unlike "/workflow ..."/"/workflow
    // create ..." which actually build something and so must go through the agent.
    if (/^\/workflow\s+trigger\s*~?\s*$/i.test(currentInput)) {
      addMessage({ id: Date.now().toString(), role: 'user', content: currentInput, timestamp: new Date() });
      chatInputRef.current?.clear();
      triggerNavigation({ actionType: 'navigate', route: '/admin?tab=workflows' });
      return;
    }

    onNotificationPermissionRequest?.();

    // Only the live editor content carries @/~ mentions — a voice-dictated overrideText is plain text.
    const mentionedClients = overrideText ? [] : (chatInputRef.current?.getMentionedClients() ?? []);
    const mentionedFiles = overrideText ? [] : (chatInputRef.current?.getMentionedFiles() ?? []);
    const mentionedTemplates = overrideText ? [] : (chatInputRef.current?.getMentionedTemplates() ?? []);
    const mentionedSkills = overrideText ? [] : (chatInputRef.current?.getMentionedSkills() ?? []);
    const mentionedRules = overrideText ? [] : (chatInputRef.current?.getMentionedRules() ?? []);

    // Stop any active dictation so trailing results don't repopulate the cleared box.
    shouldAutoSendRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      setIsListening(false);
    }

    // Collect all messages from the current session block for history
    const currentBlock = blocks.find(b => b.sessionId === currentSessionId);
    const currentMessages = currentBlock?.messages ?? [];

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: currentInput,
      timestamp: new Date(),
      attachments: readyAttachments,
    };

    // Sending a new message abandons any unconfirmed action — clear stale cards.
    // First clear pending actions on all current-session messages, then add the user message.
    currentMessages
      .filter(m => m.pendingAction)
      .forEach(m => replaceLastMessage({ ...m, pendingAction: null }));
    addMessage(userMessage);
    chatInputRef.current?.clear();
    setAttachments([]);
    setIsLoading(true);
    setError(null);
    scrollToBottom();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await agentClient.sendMessage({
        message: currentInput,
        mentionedClients,
        mentionedFiles,
        mentionedTemplates,
        mentionedSkills,
        mentionedRules,
        attachmentIds: readyAttachments.map((attachment) => attachment.id),
        context: context,
        // The server owns the complete session history. Only send a bounded
        // fallback window for new/legacy sessions so large visible transcripts
        // do not make the request fail validation.
        history: currentMessages.slice(-50),
        streaming: false,
        signal: controller.signal,
        modelSelection,
      });

      // Sticky per conversation: only overwrite when the server resolved one,
      // so a turn that establishes no client leaves the banner as-is.
      if (response.conversationClient) setConversationClient(response.conversationClient);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        toolOperations: response.toolOperations,
        pendingAction: response.pendingAction ?? null,
        emailLink: response.emailLink ?? deriveEmailLink(response.toolOperations),
        needsFileUploadTrigger: response.toolOperations?.some((op: any) => AI_FILE_UPLOAD_TOOLS.has(op.tool)),
        timestamp: new Date()
      };

      addMessage(assistantMessage);
      if (onNewMessage) onNewMessage(assistantMessage, currentSessionId);

      if (onProcessOperations && response.toolOperations) {
        setTimeout(() => onProcessOperations(response.toolOperations!), 100);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setAttachments(readyAttachments);
      setError(err.message || 'Failed to send');
      addMessage({
        id: Date.now().toString(),
        role: 'assistant',
        content: `**Error:** ${err.message}. Please try again.`,
        timestamp: new Date()
      });
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      setTimeout(() => chatInputRef.current?.focus(), 100);
      scrollToBottom();
    }
  }, [isLoading, blocks, currentSessionId, context, agentClient, addMessage, replaceLastMessage, attachments, modelSelection, onNewMessage, onNotificationPermissionRequest, onProcessOperations]);

  /** Shared by the attach button and the drop target — both end up here. */
  const uploadAttachments = useCallback(async (incoming: File[]) => {
    const files = incoming.slice(0, Math.max(0, MAX_CHAT_ATTACHMENTS - attachments.length));
    for (const file of files) {
      const temporaryId = `upload-${Date.now()}-${file.name}`;
      const pending: ChatAttachment = {
        id: temporaryId,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        status: 'uploading',
        progress: 0,
      };
      setAttachments((current) => [...current, pending]);
      try {
        const uploaded = await agentClient.uploadPrivateAttachment(file, context.sessionId, (progress) => {
          setAttachments((current) => current.map((item) => item.id === temporaryId ? { ...item, progress } : item));
        }, context.routeParams?.clientId);
        setAttachments((current) => current.map((item) => item.id === temporaryId ? uploaded : item));
      } catch (uploadError: any) {
        setAttachments((current) => current.map((item) => item.id === temporaryId ? { ...item, status: 'error' } : item));
        setError(uploadError?.message || `Failed to upload ${file.name}`);
      }
    }
  }, [agentClient, attachments.length, context.sessionId, context.routeParams?.clientId]);

  const handleAttachmentSelection = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    await uploadAttachments(files);
  }, [uploadAttachments]);

  // Dropping onto the chat panel is the entry point for "here's a recording,
  // where does it go?" — the agent's suggest_meeting_for_recording tool reads
  // the attachment this produces.
  //
  // maxFiles counts the remaining slots, not MAX_ATTACHMENTS, so dropping three
  // files when two are already attached reports the overflow instead of
  // silently discarding it in uploadAttachments' slice.
  const { isDragOver: isAttachmentDragOver, dropProps: attachmentDropProps } = useFileDrop({
    accept: CHAT_UPLOAD_ACCEPT,
    multiple: true,
    maxFiles: Math.max(0, MAX_CHAT_ATTACHMENTS - attachments.length),
    disabled: isLoading || attachments.length >= MAX_CHAT_ATTACHMENTS,
    onFiles: (files) => { void uploadAttachments(files); },
    onRejected: setError,
  });

  const removeAttachment = useCallback(async (attachment: ChatAttachment) => {
    setAttachments((current) => current.filter((item) => item.id !== attachment.id));
    if (attachment.status === 'ready') {
      try {
        await agentClient.deletePrivateAttachment(attachment.id);
      } catch {
        setError(`Could not remove ${attachment.fileName} from private storage.`);
      }
    }
  }, [agentClient]);

  const handleConfirmAction = useCallback(async (
    messageId: string,
    toolCallId: string,
    approved: boolean,
  ) => {
    // Clear the card immediately: find the message and replace it without the pendingAction.
    const currentBlock = blocks.find(b => b.sessionId === currentSessionId);
    const targetMsg = currentBlock?.messages.find(m => m.id === messageId);
    if (targetMsg) {
      replaceLastMessage({ ...targetMsg, pendingAction: null });
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await agentClient.confirmAction(toolCallId, approved, modelSelection);
      const followUp: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        toolOperations: response.toolOperations,
        pendingAction: response.pendingAction ?? null,
        meetingLink: response.meetingLink ?? null,
        emailLink: response.emailLink ?? deriveEmailLink(response.toolOperations),
        workflowLink: response.workflowLink ?? null,
        timestamp: new Date(),
      };
      addMessage(followUp);
      if (onNewMessage) onNewMessage(followUp, currentSessionId);
      if (onProcessOperations && response.toolOperations) {
        setTimeout(() => onProcessOperations(response.toolOperations!), 100);
      }
      if (response.meetingLink) {
        setTimeout(() => triggerNavigation({
          actionType: 'navigate',
          route: `/meetings/${response.meetingLink!.meetingId}`,
        }), 300);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to confirm action');
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  }, [agentClient, blocks, currentSessionId, modelSelection, onNewMessage, onProcessOperations, addMessage, replaceLastMessage]);

  const handleSelectOption = useCallback(async (
    messageId: string,
    toolCallId: string,
    optionIndex: number,
  ) => {
    // Clear the options card immediately; the follow-up message carries the
    // refined single-action confirm card (or an error) once the request resolves.
    const currentBlock = blocks.find(b => b.sessionId === currentSessionId);
    const targetMsg = currentBlock?.messages.find(m => m.id === messageId);
    if (targetMsg) {
      replaceLastMessage({ ...targetMsg, pendingAction: null });
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await agentClient.selectPendingOption(toolCallId, optionIndex, modelSelection);
      const followUp: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        toolOperations: response.toolOperations,
        pendingAction: response.pendingAction ?? null,
        timestamp: new Date(),
      };
      addMessage(followUp);
      if (onNewMessage) onNewMessage(followUp, currentSessionId);
    } catch (err: any) {
      setError(err.message || 'Failed to select option');
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  }, [agentClient, blocks, currentSessionId, modelSelection, onNewMessage, addMessage, replaceLastMessage]);

  // --- Voice input (Web Speech API — Chrome/Edge) ---
  const speechSupported = typeof window !== 'undefined' &&
    !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  const stopListening = useCallback((autoSend = false) => {
    shouldAutoSendRef.current = autoSend;
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const startListening = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;

    // Preserve anything already typed, then append dictation to it.
    const existingText = chatInputRef.current?.getText() ?? '';
    baseTranscriptRef.current = existingText ? existingText.trim() + ' ' : '';
    pendingTranscriptRef.current = '';
    shouldAutoSendRef.current = true;

    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      const fullText = (baseTranscriptRef.current + transcript).trim();
      pendingTranscriptRef.current = fullText;
      chatInputRef.current?.setText(fullText);
    };
    recognition.onerror = (event: any) => {
      console.warn('Speech recognition error:', event.error);
      shouldAutoSendRef.current = false;
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError('Microphone access was blocked. Allow mic permission in your browser to use voice.');
      } else if (event.error === 'no-speech') {
        setError('No speech detected. Try again and speak clearly.');
      }
      setIsListening(false);
    };
    recognition.onend = () => {
      setIsListening(false);
      const text = pendingTranscriptRef.current;
      if (shouldAutoSendRef.current && text) {
        shouldAutoSendRef.current = false;
        handleSendMessage(text);
      }
      pendingTranscriptRef.current = '';
    };

    recognitionRef.current = recognition;
    setError(null);
    setIsListening(true);
    try {
      recognition.start();
    } catch {
      shouldAutoSendRef.current = false;
      setIsListening(false);
    }
  }, [handleSendMessage]);

  const toggleListening = useCallback(() => {
    if (isListening) stopListening(true);
    else startListening();
  }, [isListening, startListening, stopListening]);

  // Stop dictation if the panel closes/unmounts.
  useEffect(() => {
    return () => { try { recognitionRef.current?.abort?.(); } catch { /* noop */ } };
  }, []);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    // Restore last user message to the input if it's the last message in the current block
    const currentBlock = blocks.find(b => b.sessionId === currentSessionId);
    if (currentBlock && currentBlock.messages.length > 0) {
      const last = currentBlock.messages[currentBlock.messages.length - 1];
      if (last.role === 'user') {
        chatInputRef.current?.setText(last.content);
        replaceLastMessage({ ...last }); // trigger a re-render by popping via replaceLastMessage with a noop isn't enough — we need to actually remove it
      }
    }
    chatInputRef.current?.focus();
  };

  const lastBlock = blocks[blocks.length - 1];
  // Only the true blank-slate case — no other session's history loaded above
  // it via scroll-up. Otherwise the greeting reads as a non-sequitur sitting
  // right under real prior conversation content.
  const isEmptySession = blocks.length <= 1 && (!lastBlock || lastBlock.messages.length === 0);

  // The page's client, resolved from the route. Only a fallback: it labels the
  // context before the conversation has established a client of its own.
  // Re-resolves whenever routeParams.clientId changes (including client-to-client
  // navigation while the panel stays open) — see useChatContext.ts's route-driven effect.
  const pageClientName = useClientName(context.routeParams?.clientId);

  // Once the conversation is about a client, that client IS the context — it
  // outranks the page and deliberately survives navigating elsewhere. Server-
  // resolved (AgentSession.activeClientId), so it also restores on resume.
  // Priority mirrors the server's own conversationClientId chain: what this
  // turn resolved, then what the reopened session stored, then the page. Each
  // is cleared when the session changes, so nothing outlives its conversation.
  const contextClientName = conversationClient?.name ?? loadedSessionClient?.name ?? pageClientName;

  return (
    <Box
      {...attachmentDropProps}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f8f9fa', position: 'relative' }}
    >
      {isAttachmentDragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            m: 1,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'primary.main',
            bgcolor: 'rgba(248,249,250,0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            pointerEvents: 'none',
          }}
        >
          <AttachFileIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={600}>
            Drop files to attach
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Drop a recording and I'll suggest which meeting it belongs to
          </Typography>
        </Box>
      )}
      {contextClientName && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            bgcolor: '#eef4ff',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Client: {contextClientName}
          </Typography>
        </Box>
      )}
      {error && (
        <Fade in>
          <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 0 }}>
            {error}
          </Alert>
        </Fade>
      )}

      {/* Message Area — wrapped so the "jump to present" control can be pinned to
          the bottom of the scrollable area itself, never over suggestions/input below. */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="custom-scrollbar"
        tabIndex={0}
        role="log"
        aria-label="Chat message history"
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          scrollBehavior: 'smooth',
          overflowAnchor: 'none',
          scrollbarGutter: 'stable',
          outline: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '-2px',
          },
        }}
      >
        {showHistory ? (
          <SessionsPanel
            agentClient={agentClient}
            onSelectSession={handleSelectSession}
            onBack={() => setShowHistory(false)}
          />
        ) : (
          <>
            {/* Earlier-messages status — no button; triggered by the first upward
                scroll/swipe/keypress attempt while already at the top (see the
                gesture-detection effect above). One persistent slot whose text swaps in
                place (loading → loaded) rather than separate elements
                animating in/out at different times and different places —
                that's what read as lag. "Loaded" stays until the user's next
                scroll-up attempt, not on a timer. This region is announced to
                screen readers. */}
            {(showLoadingOlder || loadedConfirmationVisible || (loadError && !showLoadingOlder)) && (
              <Box aria-live="polite" aria-atomic="true" sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                {loadError && !showLoadingOlder ? (
                  <Typography variant="caption" color="error.main">
                    Couldn't load earlier messages — scroll up again to retry
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.5, py: 0.75, borderRadius: 5,
                      bgcolor: showLoadingOlder ? 'background.paper' : 'primary.50',
                      border: showLoadingOlder ? '1px solid' : 'none',
                      borderColor: 'divider',
                      boxShadow: showLoadingOlder ? 1 : 0,
                    }}
                  >
                    {showLoadingOlder && <CircularProgress size={14} thickness={5} />}
                    <Typography
                      variant="caption"
                      color={showLoadingOlder ? 'text.secondary' : 'primary.main'}
                      sx={{ fontWeight: 500 }}
                    >
                      {showLoadingOlder ? 'Loading earlier messages…' : 'Earlier messages loaded'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Session blocks. Only the live (last) block gets the slide-in-on-arrival
                animation — historical blocks revealed by "load earlier" render in their
                final state immediately, so the scroll-position correction above doesn't
                fight a simultaneous slide/fade and look like content is still loading. */}
            {blocks.map((block, blockIdx) => {
              const isLiveBlock = blockIdx === blocks.length - 1;
              const entryClass = isLiveBlock ? 'chat-message-entry' : undefined;
              return (
              <React.Fragment key={block.sessionId}>
                {blockIdx > 0 && (
                  <Box className={entryClass}>
                    <SessionDivider date={block.date} label={block.preview} title={block.title} />
                  </Box>
                )}
                {block.messages.map(message => (
                  <React.Fragment key={message.id}>
                    <Box className={entryClass}>
                      <ChatMessage message={message} />
                      {message.pendingAction && (
                        <Box sx={{ mx: 2, mb: 1 }}>
                          <Paper
                            elevation={0}
                            sx={{
                              mt: 1, ml: 1, maxWidth: '90%',
                              p: 1.5,
                              borderRadius: '4px 14px 14px 14px',
                              border: '1px solid',
                              borderColor: block.sessionId === currentSessionId ? 'primary.main' : 'grey.300',
                              bgcolor: block.sessionId === currentSessionId ? '#f0f7ff' : 'grey.50',
                            }}
                          >
                            {block.sessionId === currentSessionId && (
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                                Confirm action
                              </Typography>
                            )}
                            {message.pendingAction.tool === 'send_sms' && message.pendingAction.params ? (
                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {message.pendingAction.params.contactName || 'Unknown contact'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {message.pendingAction.params.toPhoneNumber}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  "{message.pendingAction.params.messageBody}"
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {message.pendingAction.summary}
                              </Typography>
                            )}
                        {block.sessionId === currentSessionId ? (
                          message.pendingAction.options?.length ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {message.pendingAction.options.map((option, i) => (
                                <Chip
                                  key={i}
                                  label={option.label}
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                  disabled={isLoading}
                                  onClick={() => handleSelectOption(message.id, message.pendingAction!.toolCallId, i)}
                                  sx={{ fontWeight: 600 }}
                                />
                              ))}
                              <Chip
                                label="Cancel"
                                variant="outlined"
                                size="small"
                                disabled={isLoading}
                                onClick={() => handleConfirmAction(message.id, message.pendingAction!.toolCallId, false)}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip
                                label="Approve"
                                color="primary"
                                size="small"
                                disabled={isLoading}
                                onClick={() => handleConfirmAction(message.id, message.pendingAction!.toolCallId, true)}
                                sx={{ fontWeight: 600 }}
                              />
                              <Chip
                                label="Cancel"
                                variant="outlined"
                                size="small"
                                disabled={isLoading}
                                onClick={() => handleConfirmAction(message.id, message.pendingAction!.toolCallId, false)}
                              />
                            </Box>
                          )
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            This action is no longer available
                          </Typography>
                        )}
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  </React.Fragment>
                ))}
              </React.Fragment>
              );
            })}

            {isEmptySession && (
              <NewSessionGreeting firstName={context.currentUser?.data?.firstName} />
            )}

            {isLoading && (
              <Fade in={true} timeout={500}>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, ml: 1, maxWidth: '80%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: '12px 16px',
                      borderRadius: '18px 18px 18px 4px',
                      bgcolor: 'white',
                      border: '1px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'flex' }}>
                      <ThinkingIcon sx={{ color: 'primary.main', fontSize: 20, animation: 'pulse 1.5s infinite' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                      {THINKING_STEPS[thinkingStep]}
                    </Typography>
                  </Paper>
                </Box>
              </Fade>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Jump to present — pinned to the bottom of the message area, centered,
          so it never covers messages, suggestions, or the input controls below. */}
      {showJumpToPresent && !showHistory && (
        <Box sx={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
          <Tooltip title="Jump to present">
            <IconButton
              onClick={() => { scrollToBottom(); setShowJumpToPresent(false); }}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                color: 'primary.main',
                width: 32,
                height: 32,
                boxShadow: 2,
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'action.hover', transform: 'scale(1.1)' }
              }}
            >
              <JumpToPresentIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      </Box>{/* end message-area wrapper */}

      {/* Suggestions */}
      {(!isLoading && !showHistory && suggestions.length > 0) && (
        suggestionsHidden ? (
          <Box sx={{ px: 2, pb: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              icon={<SparkleIcon style={{ width: 14 }} />}
              label="Show suggestions"
              onClick={() => setSuggestionsHidden(false)}
              sx={{ borderStyle: 'dashed', color: 'text.secondary' }}
            />
          </Box>
        ) : (
          <Box sx={{ px: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ ml: 1, display: 'block', color: 'text.secondary' }}>
                Suggested Actions:
              </Typography>
              <Tooltip title="Hide suggestions">
                <IconButton size="small" onClick={() => setSuggestionsHidden(true)} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {suggestions.map((s, i) => (
                <Chip
                  key={i}
                  label={s}
                  onClick={() => chatInputRef.current?.setText(s)}
                  icon={<SparkleIcon style={{ width: 14 }} />}
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid #e0e0e0',
                    '&:hover': { bgcolor: '#f0f7ff', borderColor: 'primary.main', color: 'primary.main' }
                  }}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )
      )}

      {/* Input Area */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
          flexShrink: 0 // Prevent input from shrinking
        }}
      >
        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
            {attachments.map((attachment) => (
              <Box key={attachment.id} sx={{ minWidth: 130, maxWidth: '100%' }}>
                <Chip
                  size="small"
                  label={attachment.fileName}
                  color={attachment.status === 'error' ? 'error' : 'default'}
                  onDelete={attachment.status === 'uploading' ? undefined : () => removeAttachment(attachment)}
                  sx={{ maxWidth: '100%' }}
                />
                {attachment.status === 'uploading' && <LinearProgress variant="determinate" value={attachment.progress || 0} sx={{ mt: 0.25, borderRadius: 2 }} />}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          bgcolor: '#f5f5f5',
          borderRadius: 3,
          p: '8px 8px 8px 16px',
          transition: 'box-shadow 0.2s',
          border: '1px solid transparent',
          '&:focus-within': {
            bgcolor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            borderColor: 'primary.main'
          }
        }}>
          <input
            ref={attachmentInputRef}
            type="file"
            hidden
            multiple
            accept={CHAT_UPLOAD_ACCEPT}
            onChange={handleAttachmentSelection}
          />
          <Tooltip title="Attach a private file">
            <span>
              <IconButton
                onClick={() => attachmentInputRef.current?.click()}
                disabled={isLoading || attachments.length >= MAX_CHAT_ATTACHMENTS}
                aria-label="Attach a private file"
                sx={{
                  color: 'text.secondary',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <AddIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>

          <ChatMessageInput
            ref={chatInputRef}
            disabled={isLoading}
            placeholder={
              isLoading
                ? 'Please wait...'
                : isListening
                  ? 'Listening...'
                  : 'Ask Prospo AI...'
            }
            onSend={() => handleSendMessage()}
            onEmptyChange={setIsInputEmpty}
          />

          <Tooltip
            title={
              !speechSupported
                ? 'Voice input needs Chrome or Edge'
                : isLoading
                  ? 'Wait for the response to finish'
                  : isListening
                    ? 'Stop and send'
                    : 'Speak to Prospo AI'
            }
          >
            <span>
              <IconButton
                onClick={toggleListening}
                disabled={!speechSupported || isLoading}
                sx={{
                  bgcolor: isListening ? '#ffebee' : 'transparent',
                  color: isListening ? '#d32f2f' : 'text.secondary',
                  width: 32, height: 32,
                  animation: isListening ? 'pulse 1.5s infinite' : 'none',
                  '&:hover': { bgcolor: isListening ? '#ffcdd2' : 'action.hover' }
                }}
              >
                {isListening ? <MicOffIcon sx={{ fontSize: 20 }} /> : <MicIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </span>
          </Tooltip>

          {isLoading ? (
            <Tooltip title="Stop Generating">
              <IconButton
                onClick={handleStop}
                sx={{
                  bgcolor: '#ffebee',
                  color: '#d32f2f',
                  width: 32, height: 32,
                  '&:hover': { bgcolor: '#ffcdd2' }
                }}
              >
                <StopIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={() => handleSendMessage()}
              disabled={isInputEmpty && !attachments.some((attachment) => attachment.status === 'ready')}
              sx={{
                bgcolor: (!isInputEmpty || attachments.some((attachment) => attachment.status === 'ready')) ? 'primary.main' : 'action.disabledBackground',
                color: 'white',
                width: 32, height: 32,
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.1)' }
              }}
            >
              <SendIcon sx={{ fontSize: 18, ml: 0.5 }} />
            </IconButton>
          )}
        </Box>
      </Paper>

      <style>{`
        @keyframes pulse {
          0% { opacity: 0.6; transform: scale(0.95); }
          50% { opacity: 1; transform: scale(1.05); }
          100% { opacity: 0.6; transform: scale(0.95); }
        }
      `}</style>
    </Box>
  );
};
